'use client';

import { LayoutNodeStyleMap, type LayoutNode } from 'shop-ui/types/basic';
import { ELEMENT_TYPE, FEATURE_FLAG_KEYS, UI_LINK_TYPE_ENUM } from 'shop-ui/constants';
import { getElementUlId } from 'app/shop-ui/tools/getElementUlId';
import { combineComponent } from 'shop-ui/tools/combineComponent';
import { GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid/rules';
import i18n from '@shared/i18n';

export const componentName = 'RichText';

export enum OFFICIAL_FONT_SIZE_KEY {
    HEADING_LARGE = 'official-heading-large',
    HEADING_MEDIUM = 'official-heading-medium',
    HEADING_SMALL = 'official-heading-small',
    PARAGRAPH_LARGE = 'official-paragraph-large',
    PARAGRAPH_MEDIUM = 'official-paragraph-medium',
    PARAGRAPH_SMALL = 'official-paragraph-small',
    CUSTOM = 'custom',
}

export const showCutList = [
    {
        key: OFFICIAL_FONT_SIZE_KEY.HEADING_LARGE,
        type: ELEMENT_TYPE.RICH_TEXT,
        content: i18n.gettext('標題文字'),
    },
    {
        key: OFFICIAL_FONT_SIZE_KEY.HEADING_MEDIUM,
        type: ELEMENT_TYPE.RICH_TEXT,
        content: i18n.gettext('標題文字'),
    },
    {
        key: OFFICIAL_FONT_SIZE_KEY.HEADING_SMALL,
        type: ELEMENT_TYPE.RICH_TEXT,
        content: i18n.gettext('標題文字'),
    },
    {
        key: OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_LARGE,
        type: ELEMENT_TYPE.RICH_TEXT,
        content: i18n.gettext('段落文字'),
    },
    {
        key: OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_MEDIUM,
        type: ELEMENT_TYPE.RICH_TEXT,
        content: i18n.gettext('段落文字'),
    },
    {
        key: OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_SMALL,
        type: ELEMENT_TYPE.RICH_TEXT,
        content: i18n.gettext('段落文字'),
    },
];

const defaultContent = 'Lorem ipsum dolor sit amet consectetur.';

const config: LayoutNode = {
    id: '',
    type: ELEMENT_TYPE.RICH_TEXT,
    name: componentName,
    enable: true, // 通常要開啟
    next: null,
    deletable: true, // 通常要開啟
};

export const features = [
    FEATURE_FLAG_KEYS.RESIZE_HANDLE,
    FEATURE_FLAG_KEYS.RICH_TEXT,
    FEATURE_FLAG_KEYS.FONT_FAMILY,
    FEATURE_FLAG_KEYS.FONT_WEIGHT,
    FEATURE_FLAG_KEYS.FONT_SIZE,
    FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM,
    FEATURE_FLAG_KEYS.FONT_COLOR,
    FEATURE_FLAG_KEYS.FONT_STYLE,
    FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING,
    FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT,
    FEATURE_FLAG_KEYS.TEXT_ALIGNMENT,
    FEATURE_FLAG_KEYS.ENABLE_LINK,
    FEATURE_FLAG_KEYS.UI_LINK_TYPE,
    FEATURE_FLAG_KEYS.UI_LINK,
    FEATURE_FLAG_KEYS.SHOULD_OPEN_NEW_WINDOW,
    FEATURE_FLAG_KEYS.ANIMATION,
];

const defaultStyle = {
    [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'Inter',
    [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
    [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
    [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
    [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
    [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
    [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
    [FEATURE_FLAG_KEYS.ENABLE_LINK]: false,
    [FEATURE_FLAG_KEYS.UI_LINK]: '',
    [FEATURE_FLAG_KEYS.UI_LINK_TYPE]: UI_LINK_TYPE_ENUM.PAGE,
    [FEATURE_FLAG_KEYS.SHOULD_OPEN_NEW_WINDOW]: false,
} as const;

export function createRichText(
    name: string,
    content: string,
    presetStyle: LayoutNodeStyleMap[string] = {}
) {
    const id = getElementUlId();

    const layout = {
        ...config,
        id,
    };

    return {
        id,
        layout,
        style: {
            ...defaultStyle,
            [FEATURE_FLAG_KEYS.FONT_SIZE]: name,
            ...presetStyle,
        },
        content,
    };
}

export function generate(
    sectionName: string = i18n.gettext('區塊'),
    name: string = OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_MEDIUM,
    content = defaultContent,
    options?: { shouldWrap?: boolean }
) {
    const richText = createRichText(name, content);

    const shouldWrap = options?.shouldWrap ?? true;

    return combineComponent(sectionName, [richText], {
        primaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        shouldWrap,
    });
}
